import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MuiAlert from '@mui/material/Alert';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  SnackbarCloseReason,
  CardHeader,
  Divider,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControl,
  InputLabel,
  FormHelperText,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "../../assets/style.css";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { useActions } from "../../hooks/useActions";
import EncompassService from "../../services/encompassService";
import { IDefaultValues } from "../../models/configuration/plugin/IConfiguration";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";
import {
  REQUEST_TYPES,
  SAVE_LOAN_OPTIONS,
  REQUEST_TYPE_OPTIONS,
  DEFAULT_REQUEST_OPTIONS,
  PREVIEW_ATTACHMENTS_OPTIONS
} from "../../utils/constants";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { store } from "../../state/store";
import { ActionType } from "../../state/actions";
import { AlertSeverity } from "../../constants/AlertTypes";
import { DtCard } from "../../components/ui/card";
import { DtPageHeader } from "../../components/ui/header";
import DtToggleButton from "../../components/ui/toggle/DtToggleButton";

const ConfigMisc: React.FC = () => {

  const { saveConfigMiscData } = useActions();
  const [miscDefaultData, setMiscDefaultData] = useState<IDefaultValues | null>(null);
  const accessToken = useTypedSelector((state) => state.appSlice.accessToken);
  const xPathData = useTypedSelector((state) => state.xPathData.data);
  const persistedXPathData = useTypedSelector((state) => state.xPathData.persistedData);
  const pushbackData = useTypedSelector((state) => state.configPushbackDefaults.data);
  const persistedPushbackData = useTypedSelector((state) => state.configPushbackDefaults.persistedData);
  const documentBundles = useTypedSelector((state) => state.configDocumentBundles.data);
  const persistedDocumentBundles = useTypedSelector((state) => state.configDocumentBundles.persistedData);
  const eventBundles = useTypedSelector((state) => state.configEventBundles.data);
  const persistedEventBundles = useTypedSelector((state) => state.configEventBundles.persistedData);
  // Misc state
  const [selectedConfigId, setSelectedConfigId] = useState<string>("");
  const [integrationUri, setIntegrationUri] = useState("");
  const [pluginUri, setPluginUri] = useState("");
  const [tokenUri, setTokenUri] = useState("");
  const [saving, setSaving] = useState<boolean>(false);

  // State Confirmations
  const [alertOpen, setAlertOpen] = useState(false);
  const [saveError, setSaveError] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');

  const hasFetchedData = useRef(false);
  const { data, error, loading } = useConfigurationData(ConfigurationDataType.DEFAULT_VALUES);
  const mData = useMemo(() => data, [data]);
  useEffect(() => {
    if (!hasFetchedData.current && data) {
      const configId = EncompassService.getConfigId() as string;
      setSelectedConfigId(configId);
      setMiscDefaultData(mData);
      initializeScreenData();
      hasFetchedData.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Handling Saving Error 
  useEffect(() => {
    if (!saveError && data !== null) {
      setMiscDefaultData(mData);
      initializeScreenData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, saveError, mData]);

  const saveMiscConfigData = useCallback(
    async (configId: string, data: IDefaultValues) => {
      return await saveConfigMiscData(configId, data, accessToken);
    },
    [saveConfigMiscData, accessToken]
  );

  useEffect(() => {
    if (error) {
      openAlert(error, 'error');
    }
  }, [error])

  useEffect(() => {
    if (miscDefaultData) {
      saveState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miscDefaultData]);

  const updateMiscData = (key: string, value: any) => {
    setMiscDefaultData((prevData) => {
      if (!prevData) return null;
      const newData = { ...prevData };
      if (key === "RequestTypeAvailability" && value !== REQUEST_TYPES.BOTH) {
        newData.DefaultRequestType = value;
      }
      return {
        ...newData,
        [key]: value,
      };
    });
  };


  function cacheEndpoints() {
    localStorage.setItem("integrationUri", integrationUri);
    localStorage.setItem("pluginUri", pluginUri);
    localStorage.setItem("tokenUri", tokenUri);
  }

  function initializeScreenData() {
    setIntegrationUri(
      localStorage.getItem("integrationUri") ||
      (EncompassService.getRequestEndpoint() as string)
    );
    setPluginUri(
      (EncompassService.getPluginEndpoint() as string)
    );
    setTokenUri(
      localStorage.getItem("tokenUri") ||
      (EncompassService.getTokenEndpoint() as string)
    );
    if (!selectedConfigId) {
      setSelectedConfigId(getConfigId());
    }
  }

  const getConfigId = () => {
    if (selectedConfigId) return selectedConfigId;
    return EncompassService.getConfigId() as string;
  };

  // Call this to save local session redux state
  const saveState = () => {
    try {
      if (!miscDefaultData) return;
      const configPlugInData = store.getState().configPlugInData.data;
      const updatedConfigPlugInData = {
        ...configPlugInData,
        Configuration: {
          ...configPlugInData?.Configuration,
          DefaultValues: miscDefaultData,
        },
      };
      store.dispatch({
        type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
        payload: updatedConfigPlugInData
      });

    } catch (e) {
      console.log("Error Saving State: ", e);
      openAlert(`Error Saving State: ${e}`, 'error');
    }
  }

  const save = async () => {
    const globalSaved = consumeGlobalSaveHadChanges();
    cacheEndpoints();
    try {
      setSaving(true);
      const didSave = Boolean(await saveMiscConfigData(getConfigId(), miscDefaultData as IDefaultValues));
      const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
      const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
      const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
      const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
      const didSaveAny = didSave || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
      openAlert(didSaveAny ? "Successfully Saved Changes" : "No new changes to save", didSaveAny ? "success" : "info");
      setSaveError(false);
    } catch (e) {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  // Register save function for header buttons
  useConfigurationSave(() => save());

  const handleSnackBarClose = (
    _event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
    setSaveError(false);
  };

  const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  }

  return (
    <section>
      <div>
        {(loading || saving) && <ConfigLoad loading={loading || saving} />}
      </div>
      <Stack gap={2}
      >
        <DtPageHeader>Miscellaneous Configuration</DtPageHeader>
          
          <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12} xl={8}>
            <DtCard>
              <Grid container spacing={2}>
                {/* Request Section */}
                <Grid item xs={12} md={7}>
                  <Typography variant="h5" fontWeight={600} sx={{ my: 2 }}>
                    Request
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <Typography fontWeight={500}>
                        Save Loan Before Closing
                      </Typography>
                      <Tooltip title="Specify when to save the loan before closing.">
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <DtToggleButton
                      value={Array.isArray(miscDefaultData?.SaveLoanBeforeDisclosing)
                        ? miscDefaultData?.SaveLoanBeforeDisclosing[0] ?? ""
                        : miscDefaultData?.SaveLoanBeforeDisclosing ?? ""}
                      options={SAVE_LOAN_OPTIONS}
                      onChange={val => updateMiscData("SaveLoanBeforeDisclosing", val)}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <Typography fontWeight={500}>
                        Request Type
                      </Typography>
                      <Tooltip title="Select request type.">
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <DtToggleButton
                      value={miscDefaultData?.RequestTypeAvailability ?? ""}
                      options={REQUEST_TYPE_OPTIONS}
                      onChange={val => updateMiscData("RequestTypeAvailability", val)}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <Typography fontWeight={500}>
                        Default Request
                      </Typography>
                      <Tooltip title="Select default request.">
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <DtToggleButton
                      value={
                        miscDefaultData?.RequestTypeAvailability === REQUEST_TYPES.BOTH
                          ? miscDefaultData?.DefaultRequestType ?? ""
                          : miscDefaultData?.RequestTypeAvailability ?? ""
                      }
                      options={DEFAULT_REQUEST_OPTIONS}
                      onChange={val => updateMiscData("DefaultRequestType", val)}
                      size="small"
                      disabled={miscDefaultData?.RequestTypeAvailability !== REQUEST_TYPES.BOTH}
                    />
                  </Box>
                </Grid>

                {/* Vertical Divider */}
                <Grid item md="auto" sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch" }}>
                  <Divider
                    orientation="vertical"
                  />
                </Grid>

                {/* Response Section */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h5" fontWeight={600} sx={{ my: 2 }}>
                    Response
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography fontWeight={500}>
                      Always Preview Attachments
                    </Typography>
                    <Switch
                      checked={miscDefaultData?.PreviewAttachmentsAlways}
                      onChange={e => updateMiscData("PreviewAttachmentsAlways", e.target.checked)}
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DtCard>
          </Grid>
        </Grid>

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={alertOpen}
          autoHideDuration={4000}
          onClose={handleSnackBarClose}
        >
          <MuiAlert
            onClose={handleSnackBarClose}
            severity={alertSeverity}
            sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
            {alertMessage}
          </MuiAlert>
        </Snackbar>
      </Stack>
    </section>
  );
};

export default ConfigMisc;